import { useState } from 'react'
import type { JSX } from 'react'

import clsx from 'clsx'

import {
  AlertDialog,
  Button,
  Flex,
  Label,
  Paragraph,
  toast,
} from '@buffer-mono/popcorn'
import { BufferTrackerReact as BufferTracker } from '@buffer-mono/tracking-plan'

import { useIsMobileBreakpoint } from '~publish/hooks/useIsMobileBreakpoint'
import { shuffleQueue } from '~publish/legacy/general-settings/thunks/shuffleQueue'
import { useAppDispatch } from '~publish/legacy/store'
import { usePostCounts } from '~publish/pages/AllChannels/PostList/usePostCounts'
import { useSelectedChannel } from '~publish/pages/Channel/ChannelContext'

import commonStyles from '../../../GeneralSettings.module.css'
import styles from '../QueueActions.module.css'

const AlertDialogContent = ({
  channel,
  isLoading,
  hideModal,
  onConfirmShuffleQueueClick,
}: {
  channel: ReturnType<typeof useSelectedChannel>
  isLoading: boolean
  hideModal: () => void
  onConfirmShuffleQueueClick: (count: number) => Promise<void>
}): JSX.Element => {
  const { counts, loading } = usePostCounts({
    organizationId: channel.organizationId,
    channelIds: [channel.id],
  })
  if (loading || !counts) return <></>

  return (
    <AlertDialog.Content>
      <AlertDialog.Title>Shuffle Queue?</AlertDialog.Title>
      <AlertDialog.Description>
        Your first 200 queued updates for <strong>{channel.name}</strong> will
        be shuffled into a completely random order
      </AlertDialog.Description>
      <AlertDialog.Actions>
        <AlertDialog.Cancel>
          <Button onClick={hideModal} variant="tertiary">
            Cancel
          </Button>
        </AlertDialog.Cancel>
        <AlertDialog.Action>
          <Button
            onClick={() => onConfirmShuffleQueueClick(counts.queue ?? 0)}
            loading={isLoading}
            variant="primary"
            data-testid="shuffle-queue-confirm"
          >
            Shuffle Queue
          </Button>
        </AlertDialog.Action>
      </AlertDialog.Actions>
    </AlertDialog.Content>
  )
}

const ShuffleQueueAction = (): JSX.Element => {
  const dispatch = useAppDispatch()
  const channel = useSelectedChannel()

  const [isModalShowing, setModalShowing] = useState(false)
  const [isLoading, setLoading] = useState(false)

  const showModal = (): void => setModalShowing(true)
  const hideModal = (): void => setModalShowing(false)

  const isMobile = useIsMobileBreakpoint()

  if (!channel) return <></>

  const onConfirmShuffleQueueClick = async (count: number): Promise<void> => {
    const profileId = channel.id
    setLoading(true)
    const result = await dispatch(shuffleQueue({ profileId, count }))
    if (shuffleQueue.fulfilled.match(result)) {
      toast.success('Awesome! Your queue has been successfully shuffled.')
    } else {
      toast.error(
        `Sorry! Something went wrong while shuffling your queue: ${result.error.message}. Would you be up for trying again?`,
      )
    }
    BufferTracker.queueShuffled({
      organizationId: channel.organizationId,
      channel: channel.service,
      clientName: 'publishWeb',
    })
    setLoading(false)
    hideModal()
  }

  return (
    <>
      <Flex
        justify="between"
        align="center"
        className={commonStyles.fullWidth}
        gap="xl"
      >
        <Flex direction="column">
          <Label>Shuffle Queue</Label>
          <Paragraph className={commonStyles.settingsParagraph}>
            Your first 200 queued updates will be shuffled into a completely
            random order.
          </Paragraph>
        </Flex>
        <Flex
          className={clsx(commonStyles.actionsWrapper, styles.actionsWrapper)}
        >
          <Button
            className={clsx(commonStyles.actionButton, styles.actionButton)}
            size="large"
            variant="secondary"
            onClick={showModal}
            data-testid="shuffle-queue-trigger"
          >
            {isMobile ? 'Shuffle' : 'Shuffle Queue'}
          </Button>
        </Flex>
      </Flex>
      <AlertDialog open={isModalShowing}>
        <AlertDialogContent
          channel={channel}
          isLoading={isLoading}
          hideModal={hideModal}
          onConfirmShuffleQueueClick={onConfirmShuffleQueueClick}
        />
      </AlertDialog>
    </>
  )
}

export { ShuffleQueueAction }
